/**
 * NetworkSummary - Default detail panel when nothing is selected.
 * Shows entity-level KPIs and a person list grouped by zone.
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Chip, List, ListItem, ListItemAvatar, ListItemText,
  Avatar, Divider, Alert, 
} from '@mui/material';
import {
  Hub as HubIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import { ZONE_CONFIG, ZONES, MESSAGES } from '../../constants/dashboardConfig';
import { getInitials } from '../../utils/networkUtils';
import api from '../../../../services/api';

// eslint-disable-next-line max-lines-per-function
function NetworkSummary({ entityId, nodes, egoId, networkStats }) {
  const [scores, setScores] = useState(null);
  const [scoresLoading, setScoresLoading] = useState(false);
  const [scoresError, setScoresError] = useState(null);

  // Fetch entity-level behavioral scores
  useEffect(() => {
    if (!entityId) return;

    let cancelled = false;
    setScoresLoading(true);
    setScoresError(null);

    api.getEntityBehavioralScores(entityId, {
      includeDimensions: true,
      includeCodeDistribution: true,
    })
      .then((data) => {
        if (!cancelled) setScores(data);
      })
      .catch((err) => {
        if (!cancelled) setScoresError(err);
      })
      .finally(() => {
        if (!cancelled) setScoresLoading(false);
      });

    return () => { cancelled = true; };
  }, [entityId]);

  // Group nodes by zone (exclude ego)
  const zoneGroups = {
    [ZONES.INNER]: [],
    [ZONES.COMMUNITY]: [],
    [ZONES.NETWORK]: [],
  };
  (nodes || []).forEach((node) => {
    if (node.id === egoId) return;
    const zone = node.zone || ZONES.NETWORK;
    if (zoneGroups[zone]) {
      zoneGroups[zone].push(node);
    }
  });

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 0 }}>
      {/* Header */}
      <Box
        sx={{
          p: 2.5,
          background: 'linear-gradient(135deg, #100331 0%, #1a0845 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Network Summary
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          {MESSAGES.selectNode}
        </Typography>
      </Box>

      {/* KPI Cards */}
      <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        <KpiCard
          icon={<PeopleIcon />}
          label="People"
          value={networkStats?.totalPeople ?? 0}
          color="#0097B2"
        />
        <KpiCard
          icon={<HubIcon />}
          label="Connections"
          value={networkStats?.totalConnections ?? 0}
          color="#23A38F"
        />
        <KpiCard
          icon={<TrendingUpIcon />}
          label="Avg Strength"
          value={networkStats?.avgStrength ?? '0'}
          color="#D8A200"
        />
        <KpiCard
          icon={<PsychologyIcon />}
          label="Fitness Score"
          value={
            scoresLoading ? '...' :
            scores?.fitnessScore != null ? `${Math.round(scores.fitnessScore)}%` :
            'N/A'
          }
          color="#FF3749"
        />
      </Box>

      {/* Scores Error */}
      {scoresError && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Alert severity="warning" sx={{ fontSize: '0.75rem' }}>
            Unable to load behavioral scores
          </Alert>
        </Box>
      )}

      <Divider />

      {/* Person List by Zone */}
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          People by Zone
        </Typography>

        {Object.entries(zoneGroups).map(([zone, people]) => (
          <Box key={zone} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: ZONE_CONFIG[zone]?.color || '#767676',
                }}
              />
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                {ZONE_CONFIG[zone]?.label || zone}
              </Typography>
              <Chip label={people.length} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
            </Box>

            {people.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
                No people in this zone
              </Typography>
            ) : (
              <List dense disablePadding>
                {people.slice(0, 10).map((person) => (
                  <ListItem key={person.id} disableGutters sx={{ py: 0.25, px: 1 }}>
                    <ListItemAvatar sx={{ minWidth: 36 }}>
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          fontSize: '0.65rem',
                          bgcolor: ZONE_CONFIG[zone]?.color || '#767676',
                        }}
                      >
                        {getInitials(person.label)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={person.label}
                      primaryTypographyProps={{ variant: 'body2', fontSize: '0.8rem' }}
                      secondary={person.nameResolved === false ? '(hash ID)' : undefined}
                      secondaryTypographyProps={{ fontSize: '0.65rem' }}
                    />
                    {person.mention_count && (
                      <Chip
                        label={`${person.mention_count}m`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.6rem' }}
                      />
                    )}
                  </ListItem>
                ))}
                {people.length > 10 && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 5 }}>
                    +{people.length - 10} more
                  </Typography>
                )}
              </List>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function KpiCard({ icon, label, value, color }) {
  return (
    <Box
      sx={{
        p: 1.5,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        textAlign: 'center',
      }}
    >
      <Box sx={{ color, mb: 0.5, '& .MuiSvgIcon-root': { fontSize: 20 } }}>
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default NetworkSummary;
